import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  allDeletesAtom,
  allUpdatesAtom,
  allUpdatesByNodeIdAtom,
  crossAssociationTreeAtom,
  doingUpdatesAtom,
  globalMessageAtom,
  subTreeAtom,
} from './atoms'
import { useCallback, useMemo } from 'react'
import {
  createCrossAssociationRoot,
  getLangCodeForNode,
  removeCrossAssociationRoot,
  typeCounts,
} from './utility'
import { CrossRefSummaryType, UpdatesByType } from './types'
import { doUpdate } from '../nodeUpdates/utility'
import { doInBatches } from 'shared/utils'
import { useResetTree } from 'shared/hooks/trees/hooks'
import { NodeLanguageAttributeParams } from '../LanguageAttribute/types'
import { nodeForId } from '../../utility/TreeHelpers'

export function useSubtreeSummary() {
  const subtrees = useAtomValue(subTreeAtom)

  return useMemo(() => {
    return subtrees.map<CrossRefSummaryType>((subTree) => {
      return {
        title: subTree.title,
        type: subTree.type,
        ...typeCounts(subTree),
      } as CrossRefSummaryType
    })
  }, [subtrees])
}

export function useResetCrossAssociationTree() {
  const tree = useAtomValue(crossAssociationTreeAtom)
  const resetTree = useResetTree()
  return useCallback(() => {
    if (!tree) return
    resetTree({ treeId: tree.tree_id, versionId: tree.version })
  }, [tree, resetTree])
}

export function useUpdateAll() {
  const resetTree = useResetCrossAssociationTree()
  const setMessage = useSetAtom(globalMessageAtom)
  const setUpdating = useSetAtom(doingUpdatesAtom)
  const allUpdates = useAtomValue(allUpdatesAtom)

  return useCallback(async () => {
    setUpdating(true)
    const total = allUpdates.length
    let current = 0
    await doInBatches(
      allUpdates,
      (u) => {
        current += 1
        setMessage(`UPDATING: ${current}/${total}`)
        return doUpdate(u)
      },
      5,
    )
    resetTree?.()
    setUpdating(false)
  }, [allUpdates, resetTree, setMessage, setUpdating])
}

export function useDeleteAll() {
  const resetTree = useResetCrossAssociationTree()
  const setMessage = useSetAtom(globalMessageAtom)
  const setUpdating = useSetAtom(doingUpdatesAtom)
  const allUpdates = useAtomValue(allDeletesAtom)

  return useCallback(async () => {
    setUpdating(true)
    const total = allUpdates.length
    let current = 0
    await doInBatches(
      allUpdates,
      (u) => {
        current += 1
        setMessage(`DELETING: ${current}/${total}`)
        return doUpdate(u)
      },
      5,
    )
    resetTree?.()
    setUpdating(false)
  }, [resetTree, setUpdating, setMessage, allUpdates])
}

export function useCreateCrossAssociationRoot() {
  const resetTree = useResetCrossAssociationTree()
  const fullTree = useAtomValue(crossAssociationTreeAtom)

  return useCallback(
    async ({ nodeId, language }: { nodeId: string; language: string }) => {
      const rootNode = fullTree && nodeForId(nodeId, fullTree)
      if (!rootNode) return
      await createCrossAssociationRoot({ rootNode, language })
      return resetTree()
    },
    [fullTree, resetTree],
  )
}

export function useRemoveCrossAssociationRoot() {
  const fullTree = useAtomValue(crossAssociationTreeAtom)
  const resetTree = useResetCrossAssociationTree()

  return useCallback(
    async (nodeId: string) => {
      const node = fullTree && nodeForId(nodeId, fullTree)
      if (!node) return
      await removeCrossAssociationRoot(node)
      return resetTree()
    },
    [fullTree, resetTree],
  )
}

export function useUpdatesForNodeId(nodeId: string): UpdatesByType {
  const updates = useAtomValue(allUpdatesByNodeIdAtom)
  return updates[nodeId] ?? {}
}

export function useLangAttributeParams() {
  const subtrees = useAtomValue(subTreeAtom)
  const tree = useAtomValue(crossAssociationTreeAtom)

  if (!tree || !subtrees.length) return null

  return subtrees.reduce<NodeLanguageAttributeParams>(
    (acc, st) => {
      const languageCode = getLangCodeForNode(st.id, tree) ?? 'en'
      return {
        ...acc,
        branches: [...acc.branches, { nodeId: st.id, languageCode }],
      }
    },
    {
      treeId: subtrees[0].tree_id,
      versionId: subtrees[0].version,
      branches: [],
    },
  )
}
